import React from "react";
import img1 from "../../img/feb_icon.png";
import img2 from "../img/logo.png";

export default function HTML_Favicon() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🌟 HTML Favicon</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        A <strong>favicon</strong> (favorite icon) is the small icon shown in
        the browser tab, bookmarks, search results, and mobile home screens.
        Adding a favicon makes your website look professional and easy to
        recognize.
      </p>

      {/* 🔹 What is Favicon */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#ca8a04",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          📌 What Is a Favicon?
        </h3>

        <ul className="tutorial-list-points">
          <li>Small image (usually 16×16 or 32×32 pixels)</li>
          <li>Shown in browser tabs and bookmarks</li>
          <li>
            Added inside the{" "}
            <code className="highlight_code_1">&lt;head&gt;</code> section
          </li>
          <li>
            File formats: <b>.ico</b>, <b>.png</b>, <b>.svg</b>
          </li>
        </ul>
      </div>

      <img src={img1} alt="favicon example" style={{ width: "100%" }} />

      {/* 🔹 Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
          marginTop: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#0284c7",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          📌 What You Can Use as a Favicon
        </h3>

        <ul className="tutorial-list-points">
          <li>
            <b>.ico</b> — Traditional browser favicon
          </li>
          <li>
            <b>.png</b> — High quality, supports transparency
          </li>
          <li>
            <b>.svg</b> — Scalable, modern, best-quality
          </li>
          <li>
            <b>.jpg</b> — Not recommended (no transparency)
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Favicon */}
      <div className="tutorial-box ">
        <h2>1️⃣ Add a Basic Favicon — &lt;link rel="icon"&gt;</h2>

        <p>
          This is the simplest and most commonly used method for adding a
          favicon. The favicon file is usually placed inside the project root
          folder.
        </p>
        <div className="code-demo-box">
          <pre className="code-block ">
            {`<link rel="icon" type="image/x-icon" href="${img2}">`}
          </pre>
        </div>
      </div>

      {/* 2️⃣ PNG Favicons */}
      <div className="tutorial-box">
        <h2>2️⃣ PNG Favicon (High Quality)</h2>
        <p>PNG favicons provide better quality and transparency support.</p>
        <div className="code-demo-box">
          <pre className="code-block">
            {`<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">`}
          </pre>
        </div>
      </div>

      {/* 3️⃣ SVG Favicon */}
      <div className="tutorial-box">
        <h2>3️⃣ SVG Favicon — Scalable & Modern</h2>
        <p>SVG favicons are scalable and look sharp on all devices.</p>
        <div className="code-demo-box">
          <pre className="code-block">
            {`<link rel="icon" type="image/svg+xml" href="/favicon.svg">`}
          </pre>
        </div>
      </div>

      {/* 4️⃣ Apple Touch Icon */}
      <div className="tutorial-box">
        <h2>4️⃣ Apple Touch Icon — For iPhone / iPad</h2>
        <p>Apple devices need their own special icon format.</p>
        <div className="code-demo-box">
          <pre className="code-block">
            {`<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">`}
          </pre>
        </div>
      </div>

      {/* 5️⃣ Android Chrome Icons */}
      <div className="tutorial-box">
        <h2>5️⃣ Icons for Android / Chrome</h2>
        <p>
          These icons appear when users add your website to their mobile home
          screen.
        </p>
        <div className="code-demo-box">
          <pre className="code-block">
            {`<link rel="icon" sizes="192x192" href="/android-chrome-192x192.png">
<link rel="icon" sizes="512x512" href="/android-chrome-512x512.png">`}
          </pre>
        </div>
      </div>

      {/* 6️⃣ Dark / Light Mode */}
      <div className="tutorial-box">
        <h2>6️⃣ Favicon for Dark and Light Mode</h2>
        <p>You can load different favicons for light and dark themes.</p>
        <div className="code-demo-box">
          <pre className="code-block">
            {`<link rel="icon" href="/favicon-light.svg" media="(prefers-color-scheme: light)">
<link rel="icon" href="/favicon-dark.svg" media="(prefers-color-scheme: dark)">`}
          </pre>
        </div>
      </div>

      {/* 7️⃣ Web Manifest */}
      <div className="tutorial-box">
        <h2>7️⃣ Web Manifest — Required for PWA</h2>
        <p>The manifest file is required for Progressive Web Apps.</p>
        <div className="code-demo-box">
          <pre className="code-block">
            {`<link rel="manifest" href="/site.webmanifest">`}
          </pre>
        </div>
      </div>

      {/* Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Favicon</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>favicon.ico</b> → Default browser icon
          </li>
          <li>
            <b>PNG</b> → High-quality, multiple sizes
          </li>
          <li>
            <b>SVG</b> → Best for modern browsers
          </li>
          <li>
            <b>Apple Touch Icon</b> → For iOS devices
          </li>
          <li>
            <b>Android Icons</b> → For mobile home screens
          </li>
          <li>
            <b>Manifest</b> → Needed for PWA apps
          </li>
        </ul>
      </div>
    </div>
  );
}
